import React, { useCallback, useContext, useEffect, useState } from "react";

import { UiStateContext } from "../../contexts/ui-state/UiStateContext";
import useContentStack, { SimpleContentQuery } from "../../hooks/contentstack-api/useContentStack";
import useFeatureToggle from "../../hooks/use-feature-toggle/useFeatureToggle";
import { AlertsContent, FeatureToggle, GlobalAlertsContent, IconPack } from "../../models/app-data-model";
import ChAlert from "./ch-alert/ch-alert";
import GlobalAlert from "./global-alert/global-alert";

const getBlob = (a: AlertsContent): string => {
  a.alert = a.page_alerts != null ? a.page_alerts : a.alert;

  return `${a.alert.type}
    ${a.alert.headline}
    ${a.alert.body}
    ${a.secondary_button?.text}
    ${a.secondary_button?.reference?.[0]?.url}
    ${a.primary_button?.text}
    ${a.primary_button?.reference?.[0]?.url}`;
};

export type alertsProps = {
  iconPack: IconPack[];
  featureToggles: FeatureToggle[];
  showPageAlertsOnly?: boolean;
};

const GlobalAlerts: React.FC<alertsProps> = ({ iconPack, featureToggles, showPageAlertsOnly }: alertsProps) => {
  const globalAlertsFeatureEnabled = useFeatureToggle("global-alert", featureToggles);
  const alertRedesignFeatureEnabled = useFeatureToggle("global-alert-redesign", featureToggles);
  const uiStateContext = useContext(UiStateContext);
  const [pageAlerts, setPageAlerts] = useState<AlertsContent[]>([]);
  const [subsiteAlerts, setSubsiteAlerts] = useState<AlertsContent[]>([]);
  const [openAlerts, setOpenAlerts] = useState<AlertsContent[]>([]);

  const globalAlertsQuery: SimpleContentQuery[] = showPageAlertsOnly
    ? []
    : [
        {
          contentType: "global_alerts_spectrumhealth_org",
          entryUid: process.env.GATSBY_GLOBAL_ALERTS_UUID,
          referencedFields: [
            "alerts.primary_button.reference",
            "alerts.primary_button.reference.default_link.reference",
            "alerts.primary_button.reference.southeast_region_link.reference",
            "alerts.primary_button.reference.southwest_region_link.reference",
            "alerts.primary_button.reference.west_region_link.reference",
            "alerts.secondary_button.reference",
            "alerts.secondary_button.reference.default_link.reference",
            "alerts.secondary_button.reference.southeast_region_link.reference",
            "alerts.secondary_button.reference.southwest_region_link.reference",
            "alerts.secondary_button.reference.west_region_link.reference"
          ]
        }
      ];

  const latestAlerts = useContentStack(globalAlertsQuery);

  const updateAlertContainerHeight = useCallback(() => {
    /* istanbul ignore else */
    if (typeof document !== "undefined") {
      const alertContainerElement = document.getElementById("alert-container") as HTMLElement;
      uiStateContext?.setAlertContainerHeight?.(alertContainerElement?.getBoundingClientRect().height);
    }
  }, [uiStateContext]);

  useEffect(() => {
    if (uiStateContext.pageAlerts) {
      setPageAlerts(uiStateContext.pageAlerts);
    }
  }, [uiStateContext.pageAlerts]);

  useEffect(() => {
    if (uiStateContext.subsiteAlerts && uiStateContext.subsiteAlerts.length > 0) {
      setSubsiteAlerts(uiStateContext.subsiteAlerts);
    }
  }, [uiStateContext.subsiteAlerts]);

  // Delays alerts visibility by 1 sec
  const showGlobalAlerts = useCallback(() => {
    setTimeout(() => {
      uiStateContext?.setShowAlerts?.(true);

      updateAlertContainerHeight();
    }, 1000);
  }, [uiStateContext, updateAlertContainerHeight]);

  const getOpenAlerts = useCallback((): AlertsContent[] => {
    showGlobalAlerts();

    return (latestAlerts?.responses?.find((r) => r.content)?.content as GlobalAlertsContent)?.alerts?.filter((value) => {
      if (typeof localStorage !== "undefined") {
        updateAlertContainerHeight();
        return localStorage.getItem(getBlob(value)) !== "closed";
      } else {
        return true;
      }
    });
  }, [latestAlerts, showGlobalAlerts, updateAlertContainerHeight]);

  useEffect(() => {
    uiStateContext.setAlertCount?.(openAlerts?.length);
  }, [openAlerts, getOpenAlerts, uiStateContext.setAlertCount]);

  useEffect(() => {
    setOpenAlerts(getOpenAlerts());
  }, [getOpenAlerts]);

  const closeAlertCallback = useCallback(
    (alert: AlertsContent) => {
      window?.localStorage?.setItem(getBlob(alert), "closed");
      setOpenAlerts(getOpenAlerts());
    },
    [getOpenAlerts]
  );

  if (!globalAlertsFeatureEnabled) {
    return null;
  }

  const alertRenderings = openAlerts?.map((alert) => {
    const key = `alert-${alert._metadata?.uid}`;
    return alertRedesignFeatureEnabled ? (
      <ChAlert
        iconPack={iconPack}
        alert={alert}
        key={key}
        index={key}
        closeAlertCallback={closeAlertCallback}
        featureToggles={featureToggles}
      />
    ) : (
      <GlobalAlert iconPack={iconPack} alert={alert} key={key} index={key} closeAlertCallback={closeAlertCallback} />
    );
  });

  const pageAlertRenderings = pageAlerts?.map((alert) => {
    if (localStorage?.getItem(getBlob(alert)) !== "closed" && alert.alert.headline) {
      const key = `alert-${alert._metadata?.uid}`;
      return alertRedesignFeatureEnabled ? (
        <ChAlert
          iconPack={iconPack}
          alert={alert}
          key={key}
          index={key}
          closeAlertCallback={closeAlertCallback}
          featureToggles={featureToggles}
        />
      ) : (
        <GlobalAlert iconPack={iconPack} alert={alert} key={key} index={key} closeAlertCallback={closeAlertCallback} />
      );
    }
  });

  const subsiteAlertRenderings = subsiteAlerts?.map((alert) => {
    if (localStorage?.getItem(getBlob(alert)) !== "closed" && alert.alert.headline) {
      const key = `alert-${alert._metadata?.uid}`;
      return alertRedesignFeatureEnabled ? (
        <ChAlert
          iconPack={iconPack}
          alert={alert}
          key={key}
          index={key}
          closeAlertCallback={closeAlertCallback}
          featureToggles={featureToggles}
        />
      ) : (
        <GlobalAlert iconPack={iconPack} alert={alert} key={key} index={key} closeAlertCallback={closeAlertCallback} />
      );
    }
  });

  return (
    <>
      {alertRenderings}
      {pageAlertRenderings}
      {subsiteAlertRenderings}
    </>
  );
};

export default GlobalAlerts;
